import {createSelector} from 'reselect';

import {
  ILoadedCustomViewsAction,
  ILoadedViewAction,
  ILoadedViewsAction,
  ILoadedWorkbooksAction,
  TableauActions,
  TableauActionTypes,
} from '../actions/tableauActions';
import {ApiStatus, TableauWorkbook} from '../../models';
import {IState} from './index';

interface IWorkbook extends TableauWorkbook {
  integrationUid: string;
  id: string;
  name: string;
  [key: string]: any;
}

interface IView extends IWorkbook {
  workbookId: string;
}

interface ICustomView extends IView {
  viewId: string;
}

export const initialTableauState: ITableauState = {
  loadingWorkbooksStatus: null,
  loadingViewsStatus: null,
  loadingViewStatus: null,
  loadingCustomViewsStatus: null,
  workbooks: null,
  views: null,
  customViews: null,
  error: null,
  loadingTrustedTicketStatus: null,
  trustedTicket: null,
  trustedTicketError: null,
  uploadingThumbnailStatus: null,
  thumbnailFile: null,
  thumbnailError: null,
  loadingViewDataStatus: null,
  viewData: null,
  progress: 0,
}

export interface ITableauState {
  loadingWorkbooksStatus: ApiStatus | null;
  loadingViewsStatus: ApiStatus | null,
  loadingViewStatus: ApiStatus | null,
  loadingCustomViewsStatus: ApiStatus | null,
  workbooks: IWorkbook[] | null,
  views: IView[] | null,
  customViews: ICustomView[] | null,
  error: any;
  loadingTrustedTicketStatus: ApiStatus | null;
  trustedTicket: string | null;
  trustedTicketError: any;
  uploadingThumbnailStatus: ApiStatus | null;
  thumbnailFile: any;
  thumbnailError: any;
  loadingViewDataStatus: ApiStatus | null;
  viewData: any;
  progress: number;
}

/*
 * This reducer is used in order to populate the select boxes in the Tableau Blocks
 * in the Report, with the available workbooks and views.
 */
export default function tableauReducer(
  state: ITableauState = initialTableauState,
  action: TableauActions
): ITableauState {
  switch (action.type) {
    case TableauActionTypes.LOAD_WORKBOOKS:
      return {
        ...state,
        loadingWorkbooksStatus: ApiStatus.LOADING,
      }
    case TableauActionTypes.LOADING_WORKBOOKS_FAILED:
      return {
        ...state,
        loadingWorkbooksStatus: ApiStatus.FAILED,
        error: action.payload.error,
      }
    case TableauActionTypes.UNSET_LOAD_WORKBOOKS_STATUS:
      return {
        ...state,
        loadingWorkbooksStatus: null,
      }
    case TableauActionTypes.LOADED_WORKBOOKS:
      return handleLoadedWorkbooks(state, action);
    case TableauActionTypes.UNSET_WORKBOOKS:
      /*
       * if workbooks are unset, everything related to them should be unset as well
       */
      return initialTableauState;
    case TableauActionTypes.LOAD_VIEWS:
      return {
        ...state,
        loadingViewsStatus: ApiStatus.LOADING,
      }
    case TableauActionTypes.LOADING_VIEWS_FAILED:
      return {
        ...state,
        loadingViewsStatus: ApiStatus.FAILED,
      }
    case TableauActionTypes.LOADED_VIEWS:
      return handleLoadedViews(state, action);
    case TableauActionTypes.LOAD_VIEW:
      return {
        ...state,
        loadingViewStatus: ApiStatus.LOADING,
      }
    case TableauActionTypes.LOADING_VIEW_FAILED:
      return {
        ...state,
        loadingViewStatus: ApiStatus.FAILED,
      }
    case TableauActionTypes.LOADED_VIEW:
      return handleLoadedView(state, action);
    case TableauActionTypes.LOAD_VIEW_DATA:
      return {
        ...state,
        loadingViewDataStatus: ApiStatus.LOADING,
      }
    case TableauActionTypes.LOADING_VIEW_DATA_FAILED:
      return {
        ...state,
        loadingViewDataStatus: ApiStatus.FAILED,
      }
    case TableauActionTypes.LOADED_VIEW_DATA:
      return {
        ...state,
        loadingViewDataStatus: ApiStatus.LOADED,
        viewData: action.payload.result,
      }
    case TableauActionTypes.LOAD_CUSTOM_VIEWS:
      return {
        ...state,
        loadingCustomViewsStatus: ApiStatus.LOADING,
      }
    case TableauActionTypes.LOADING_CUSTOM_VIEWS_FAILED:
      return {
        ...state,
        loadingCustomViewsStatus: ApiStatus.FAILED,
      }
    case TableauActionTypes.LOADED_CUSTOM_VIEWS:
      return handleLoadedCustomViews(state, action);
    case TableauActionTypes.REQUEST_TRUSTED_TICKET:
      return {
        ...state,
        trustedTicket: null,
        trustedTicketError: null,
      }
    case TableauActionTypes.REQUEST_TRUSTED_TICKET_LOADING:
      return {
        ...state,
        loadingTrustedTicketStatus: ApiStatus.LOADING,
      }
    case TableauActionTypes.REQUEST_TRUSTED_TICKET_SUCCESS:
      return {
        ...state,
        loadingTrustedTicketStatus: ApiStatus.LOADED,
        trustedTicket: action.payload.result
      }
    case TableauActionTypes.REQUEST_TRUSTED_TICKET_FAILED:
      return {
        ...state,
        loadingTrustedTicketStatus: ApiStatus.FAILED,
        trustedTicketError: action.payload.error,
      }
    case TableauActionTypes.UPLOAD_THUMBNAIL:
      return {
        ...state,
        uploadingThumbnailStatus: ApiStatus.LOADING,
      }
    case TableauActionTypes.UPLOAD_THUMBNAIL_FAILED:
      return {
        ...state,
        uploadingThumbnailStatus: ApiStatus.FAILED,
        thumbnailError: action.payload.error,
      }
    case TableauActionTypes.UPLOAD_THUMBNAIL_SUCCESS:
      return {
        ...state,
        uploadingThumbnailStatus: ApiStatus.LOADED,
        thumbnailFile: action.payload.result,
      }
    case TableauActionTypes.UPLOAD_THUMBNAIL_LOADING:
      return {
        ...state,
        uploadingThumbnailStatus: ApiStatus.LOADING,
      }
    case TableauActionTypes.UPLOAD_THUMBNAIL_PROGRESS:
      return {
        ...state,
        progress: action.payload.progress,
      }
    default:
      return state;
  }
}

// Functions

function handleLoadedWorkbooks(state: ITableauState, action: ILoadedWorkbooksAction): ITableauState {
  const { integrationUid, result } = action.payload;

  const oldWorkbooks = state
    .workbooks?.filter((workbook: IWorkbook) => (workbook.integrationUid !== integrationUid)) || [];
  const newWorkbooks = result.map((workbook: IWorkbook) => ({ ...workbook, integrationUid }));

  return {
    ...state,
    loadingWorkbooksStatus: ApiStatus.LOADED,
    workbooks: [
      ...oldWorkbooks,
      ...newWorkbooks,
    ],
    error: null,
  }
}

function handleLoadedViews(state: ITableauState, action: ILoadedViewsAction): ITableauState {
  const { integrationUid, workbookId, result } = action.payload;

  const oldViews = state.views || [];
  const newViews = result.map((view: IView) => ({ ...view, workbookId, integrationUid }));

  return {
    ...state,
    loadingViewsStatus: ApiStatus.LOADED,
    views: [
      ...oldViews,
      ...newViews,
    ],
  }
}

function handleLoadedView(state: ITableauState, action: ILoadedViewAction): ITableauState {
  const { integrationUid, result } = action.payload;
  const workbookId = result.workbook_id;

  const newViews = state.views?.map((view: IView) => {
    if (view.id === result.id) {
      return {...view, ...result};
    }
    return view;
  }) || [{...result, workbookId, integrationUid}];

  return {
    ...state,
    loadingViewStatus: ApiStatus.LOADED,
    views: newViews,
  }
}

function handleLoadedCustomViews(state: ITableauState, action: ILoadedCustomViewsAction): ITableauState {
  const { integrationUid, result } = action.payload;

  if(!result) {
    return state;
  }

  const oldCustomViews = state.customViews || [];
  const newCustomViewIds = new Set(result.map((view: any) => view.id));
  const filteredOldCustomViews = oldCustomViews.filter(view => !newCustomViewIds.has(view.id));

  const newCustomViews = result.map((customView: ICustomView) => ({ ...customView, integrationUid }));

  return {
    ...state,
    loadingCustomViewsStatus: ApiStatus.LOADED,
    customViews: [
      ...filteredOldCustomViews,
      ...newCustomViews,
    ],
  }
}

// Selectors

export const getIntegrationWorkbooksSelector = ((integrationUid: string) => createSelector(
  (state: IState) => state.tableau.workbooks,
  (workbooks: IWorkbook[] | null): IWorkbook[] | null => {
    return workbooks?.filter((workbook: IWorkbook) => (workbook.integrationUid === integrationUid)) || null;
  },
));

export const getIntegrationViewsSelector = ((integrationUid: string, workbookId: string) => createSelector(
  (state: IState) => state.tableau.views,
  (views: IView[] | null): IView[] | null => {
    return views?.filter((view: IView) =>
      (view.integrationUid === integrationUid) && (view.workbookId === workbookId)) || null;
  },
));
